import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";
import gql from "graphql-tag";
import { useQuery, useApolloClient } from "@apollo/react-hooks";
import { gqlType } from "@hifieng/common";
import AppError from "../errors/AppError";
import { IOrganizations } from "../types/OrganizationTypes";
import useDeepCompareEffect from "use-deep-compare-effect";
import moment from "moment-timezone";

const PIPELINE_KM_POSTS_BATCH_SIZE = 25;
const SLOW_OPERATION_THRESHOLD_MS = 120;

const getNowMs = (): number => {
  if (typeof performance !== "undefined") {
    return performance.now();
  }

  return Date.now();
};

export const GET_ORGANIZATIONS_DATA = gql`
  query {
    organizations {
      id
      name
      imageUrl
      utcOffset
      timezoneAbbr
      pipelines {
        id
        name
        color
        segments {
          id
          post
          coordinates {
            latitude
            longitude
          }
        }
        assets {
          id
          type
          post
          coordinates {
            latitude
            longitude
          }
        }
        interfaceOptions {
          hideFromMapLegend
          hideHeartbeat
          initialFocus
          zIndex
          hidePostMarkers
        }
        chKpMaps {
          id
          name
          ch
          kp
          precision
          step
        }
      }
    }
  }
`;

export const GET_PIPELINE_KM_POSTS = gql`
  query GetPipelineKmPosts($pipelineIds: [String!]!) {
    pipelineKmPosts(pipelineIds: $pipelineIds) {
      id
      kmPosts {
        post
        coordinates {
          latitude
          longitude
        }
      }
    }
  }
`;

export type OrganizationResponseDataType = {
  id: string;
  name: string;
  imageUrl: string;
  pipelines: Array<gqlType.Pipeline>;
  pointsOfInterest: Array<gqlType.PointOfInterest>;
};

type KmPostsDataType = {
  pipelineKmPosts: Array<gqlType.PipelineKmPostsResult>;
};

const initialContext: IOrganizations = {
  activeOrg: undefined,
  setActiveOrg: () => {
    throw new Error("Organization context has not yet been initialized.");
  },
  organizations: undefined
};

type PropsType = {
  children: React.ReactNode;
};

const createPipelineIdBatches = (
  pipelineIds: Array<string>,
  batchSize: number
): Array<Array<string>> => {
  const batches: Array<Array<string>> = [];
  for (let index = 0; index < pipelineIds.length; index += batchSize) {
    batches.push(pipelineIds.slice(index, index + batchSize));
  }

  return batches;
};

const applyDaylightSavings = (org?: gqlType.Organization) => {
  if (org !== undefined) {
    // Uses IANA standard for timezone names. Long name needed for Daylight Savings to be applied.
    // utcOffset setting from config is ignored if IANA long name provided as setting.
    if (moment.tz.zone(org.timezoneAbbr)) {
      // MST works here because there is only one MST. CST and PST don't work because there are multiple of those
      const momentObj = moment.tz(org.timezoneAbbr);
      const zoneAbbr = momentObj.zoneAbbr();
      const minutesInHour = 60;
      const padding = 2;

      // Orgs with MST (any IANA abbreviations) will be skipped using the condition below.
      // If org timezone already calculated this this will also be skipped since new timezone abbreviation is being used.
      // assign timezone abbreviation and calculate utc offset if the config abbr is different from actual ie. config is not abbreviated
      if (zoneAbbr !== org.timezoneAbbr) {
        const utcOffset = momentObj.utcOffset();
        org.timezoneAbbr = zoneAbbr;
        const sign = utcOffset >= 0 ? "+" : "-";
        const absOffset = Math.abs(utcOffset);
        const hour = Math.floor(absOffset / minutesInHour);
        const minutes = absOffset % minutesInHour;
        const paddedHour = String(hour).padStart(padding, "0");
        const paddedMinutes = String(minutes).padStart(padding, "0");
        org.utcOffset = `${sign}${paddedHour}:${paddedMinutes}`;
        // console.log(`New utcOffset for ${org.id}:`, org.utcOffset)
      }
    }
  }
};

const findOrgById = (
  orgId: string,
  organizations?: Array<gqlType.Organization>
) => {
  const activeOrg =
    organizations && organizations.find(org => org.id === orgId);

  return activeOrg;
};

const mergePipelinesWithKmPosts = (
  orgs: Array<gqlType.Organization>,
  kmPostsResults: Array<gqlType.PipelineKmPostsResult>
): Array<gqlType.Organization> => {
  const kmPostsById = new Map<string, Array<gqlType.Locations>>();
  kmPostsResults.forEach(result => {
    kmPostsById.set(result.id, result.kmPosts);
  });

  const totalPipelineCount = orgs.reduce(
    (count: number, org: gqlType.Organization) =>
      count + Number(org.pipelines.length),
    0
  );
  const matchedPipelineCount = orgs.reduce(
    (count: number, org: gqlType.Organization) =>
      count +
      Number(
        org.pipelines.filter((pipeline: gqlType.Pipeline) =>
          Boolean(kmPostsById.get(pipeline.id))
        ).length
      ),
    0
  );

  console.log("OrganizationProvider merge debug", {
    organizationCount: orgs.length,
    totalPipelineCount,
    kmPostsResultCount: kmPostsResults.length,
    matchedPipelineCount
  });

  return orgs.map(org => ({
    ...org,
    pipelines: org.pipelines.map(pipeline => ({
      ...pipeline,
      kmPosts: kmPostsById.get(pipeline.id) || pipeline.kmPosts
    }))
  }));
};

export const OrganizationContext = React.createContext(initialContext);
export const useOrganizationContext = () => useContext(OrganizationContext);
export const OrganizationProvider = (props: PropsType) => {
  const apolloClient = useApolloClient();
  const { error, loading, data } = useQuery<IOrganizations>(
    GET_ORGANIZATIONS_DATA
  );

  useEffect(() => {
    if (data && data.organizations) {
      const startMs = getNowMs();
      data.organizations.forEach(applyDaylightSavings);
      const durationMs = getNowMs() - startMs;

      if (durationMs >= SLOW_OPERATION_THRESHOLD_MS) {
        console.warn("OrganizationProvider DST apply slow", {
          organizationCount: data.organizations.length,
          durationMs
        });
      }
    }
  }, [data]);

  // Collect all pipeline ids once orgs are loaded so we can batch-fetch km posts.
  const allPipelineIds = useMemo(() => {
    if (!data || !data.organizations) return [];
    const pipelineIds = data.organizations.reduce(
      (acc: Array<string>, org: gqlType.Organization) =>
        acc.concat(org.pipelines.map(p => p.id)),
      []
    );

    return Array.from(new Set(pipelineIds));
  }, [data]);

  // Stable dependency key for the pipeline id set.
  // We use this to avoid duplicate fetches when React/Apollo re-create arrays with same ids.
  const allPipelineIdsKey = useMemo(() => allPipelineIds.join("|"), [
    allPipelineIds
  ]);

  const [kmPostsData, setKmPostsData] = useState<KmPostsDataType>();
  const [kmPostsQueryError, setKmPostsQueryError] = useState<Error>();
  const [kmPostsLoading, setKmPostsLoading] = useState<boolean>(false);

  // requestId and key refs are used to prevent overlapping duplicate runs:
  // - inFlightKey: same key is already being fetched
  // - lastFetchedKey: same key already completed
  // - requestId: stale async responses are ignored
  const kmPostsRequestIdRef = useRef<number>(0);
  const kmPostsInFlightKeyRef = useRef<string>("");
  const kmPostsLastFetchedKeyRef = useRef<string>("");

  useEffect(() => {
    let isCancelled = false;
    let requestId = 0;

    if (allPipelineIds.length === 0) {
      kmPostsInFlightKeyRef.current = "";
      kmPostsLastFetchedKeyRef.current = "";
      setKmPostsData(undefined);
      setKmPostsQueryError(undefined);
      setKmPostsLoading(false);

      return () => {
        isCancelled = true;
      };
    }

    // Skip when this exact pipeline set is already in progress or already fetched.
    if (
      kmPostsInFlightKeyRef.current === allPipelineIdsKey ||
      kmPostsLastFetchedKeyRef.current === allPipelineIdsKey
    ) {
      console.log("OrganizationProvider kmPosts batch fetch skipped", {
        pipelineIdCount: allPipelineIds.length,
        inFlight: kmPostsInFlightKeyRef.current === allPipelineIdsKey,
        alreadyFetched: kmPostsLastFetchedKeyRef.current === allPipelineIdsKey
      });

      return () => {
        isCancelled = true;
      };
    }

    // Lambda/API Gateway hard limit: 6MB response payload.
    const fetchKmPostsInBatches = async () => {
      requestId = kmPostsRequestIdRef.current + 1;
      kmPostsRequestIdRef.current = requestId;
      kmPostsInFlightKeyRef.current = allPipelineIdsKey;

      const fetchStartMs = getNowMs();
      setKmPostsLoading(true);
      setKmPostsQueryError(undefined);

      const pipelineIdBatches = createPipelineIdBatches(
        allPipelineIds,
        PIPELINE_KM_POSTS_BATCH_SIZE
      );

      console.log("OrganizationProvider kmPosts batch fetch start", {
        requestId,
        pipelineIdCount: allPipelineIds.length,
        batchCount: pipelineIdBatches.length,
        batchSize: PIPELINE_KM_POSTS_BATCH_SIZE
      });

      const mergedRows = await pipelineIdBatches.reduce(
        async (
          rowsPromise: Promise<Array<gqlType.PipelineKmPostsResult>>,
          pipelineIdsBatch: Array<string>,
          batchIndex: number
        ) => {
          const rows = await rowsPromise;

          // Ignore stale work when effect unmounts or a newer request started.
          if (isCancelled || requestId !== kmPostsRequestIdRef.current) {
            return rows;
          }

          const batchStartMs = getNowMs();

          const batchResult = await apolloClient.query<KmPostsDataType>({
            query: GET_PIPELINE_KM_POSTS,
            variables: { pipelineIds: pipelineIdsBatch },
            fetchPolicy: "cache-first" // cache-first does "Serve from cache if available. Only hit network if cache misses.". KM posts do not change so this is the best option to reduce network calls. If the cache is empty, it will hit the network. Reduces how often graphql lambda queried for coordinates.
          });

          // Re-check after await in case request was superseded while network/cache resolved.
          if (isCancelled || requestId !== kmPostsRequestIdRef.current) {
            return rows;
          }

          const batchRows =
            batchResult.data && batchResult.data.pipelineKmPosts
              ? batchResult.data.pipelineKmPosts
              : [];

          const batchDurationMs = getNowMs() - batchStartMs;
          console.log("OrganizationProvider kmPosts batch done", {
            requestId,
            batchIndex: batchIndex + 1,
            batchCount: pipelineIdBatches.length,
            pipelineIdsInBatch: pipelineIdsBatch.length,
            rowsReturned: batchRows.length,
            durationMs: batchDurationMs
          });

          return rows.concat(batchRows);
        },
        Promise.resolve<Array<gqlType.PipelineKmPostsResult>>([])
      );

      if (!isCancelled && requestId === kmPostsRequestIdRef.current) {
        kmPostsInFlightKeyRef.current = "";
        kmPostsLastFetchedKeyRef.current = allPipelineIdsKey;
        setKmPostsData({ pipelineKmPosts: mergedRows });
        setKmPostsLoading(false);

        const fetchDurationMs = getNowMs() - fetchStartMs;
        if (fetchDurationMs >= SLOW_OPERATION_THRESHOLD_MS) {
          console.warn("OrganizationProvider kmPosts batch fetch slow", {
            requestId,
            durationMs: fetchDurationMs,
            mergedRowCount: mergedRows.length
          });
        } else {
          console.log("OrganizationProvider kmPosts batch fetch complete", {
            requestId,
            durationMs: fetchDurationMs,
            mergedRowCount: mergedRows.length
          });
        }
      }
    };

    fetchKmPostsInBatches().catch((queryError: unknown) => {
      if (!isCancelled && requestId === kmPostsRequestIdRef.current) {
        kmPostsInFlightKeyRef.current = "";
        setKmPostsLoading(false);
        setKmPostsQueryError(
          queryError instanceof Error
            ? queryError
            : new Error("Pipeline kmPosts query failed.")
        );
      }
    });

    return () => {
      isCancelled = true;
      if (
        requestId === kmPostsRequestIdRef.current &&
        kmPostsInFlightKeyRef.current === allPipelineIdsKey
      ) {
        kmPostsInFlightKeyRef.current = "";
      }
    };
  }, [apolloClient, allPipelineIds, allPipelineIdsKey]);

  const [activeOrgId, setActiveOrgId] = useState<string>();
  const [organizations, setOrganizations] = useState<
    Array<gqlType.Organization>
  >();

  // Combined loading ensures provider doesn't expose partial context while km posts are still fetching.
  const isLoading = loading || kmPostsLoading;
  const refreshCountRef = useRef<number>(0);

  useEffect(() => {
    refreshCountRef.current = refreshCountRef.current + 1;
    console.log("OrganizationProvider refresh debug", {
      refreshCount: refreshCountRef.current,
      loadingOrganizations: loading,
      loadingKmPosts: kmPostsLoading,
      isLoading,
      organizationCount:
        data && data.organizations ? data.organizations.length : 0,
      pipelineIdCount: allPipelineIds.length,
      kmPostsRowCount:
        kmPostsData && kmPostsData.pipelineKmPosts
          ? kmPostsData.pipelineKmPosts.length
          : 0
    });
  }, [loading, kmPostsLoading, isLoading, data, kmPostsData, allPipelineIds]);

  if (error) {
    throw new AppError(
      "Organization information could not be retrieved",
      error.message
    );
  }

  if (kmPostsQueryError) {
    throw new AppError(
      "Pipeline kmPosts could not be retrieved",
      kmPostsQueryError.message
    );
  }

  // The function that allows other parts of the app to change the organization
  // useCallback is used so that the components that implement this context don't render every time
  // this provider re-renders.
  const setActiveOrg = useCallback(
    (orgId: string) => {
      if (!organizations) {
        throw new Error(
          "You can't switch organizations while the application is loading"
        );
      }
      if (!findOrgById(orgId, organizations)) {
        throw new AppError(
          `Cannot switch to Organization "${orgId}" because it does not exist`,
          "Please refresh your browser and try again."
        );
      }

      setActiveOrgId(orgId);
      localStorage.setItem("hifi:activeOrg", orgId);
    },
    [organizations]
  );

  // useQuery has an internal caching mechanism that causes `data` to update more often than
  // necessary. The LiveStatus polling queries cause this provider to update as well. Doing a deep
  // comparison is a good way to check if the organizations have actually changed.
  useDeepCompareEffect(() => {
    if (data) {
      const orgs = data.organizations;
      const hasKmPostsRows = Boolean(
        kmPostsData &&
          kmPostsData.pipelineKmPosts &&
          kmPostsData.pipelineKmPosts.length > 0
      );

      console.log("OrganizationProvider organizations query debug", {
        hasOrganizations: Boolean(orgs),
        organizationCount: orgs ? orgs.length : 0,
        pipelineIdsRequested: allPipelineIds.length,
        hasKmPostsData: Boolean(kmPostsData && kmPostsData.pipelineKmPosts),
        kmPostsRowCount:
          kmPostsData && kmPostsData.pipelineKmPosts
            ? kmPostsData.pipelineKmPosts.length
            : 0
      });

      if (
        orgs &&
        hasKmPostsRows &&
        kmPostsData &&
        kmPostsData.pipelineKmPosts
      ) {
        const mergeStartMs = getNowMs();
        const mergedOrganizations = mergePipelinesWithKmPosts(
          orgs,
          kmPostsData.pipelineKmPosts
        );
        const mergeDurationMs = getNowMs() - mergeStartMs;

        if (mergeDurationMs >= SLOW_OPERATION_THRESHOLD_MS) {
          console.warn("OrganizationProvider merge slow", {
            organizationCount: orgs.length,
            kmPostsRowCount: kmPostsData.pipelineKmPosts.length,
            durationMs: mergeDurationMs
          });
        }

        setOrganizations(mergedOrganizations);
      } else {
        // Fallback path while kmPosts are not available yet (or empty result).
        setOrganizations(orgs);
      }

      console.log(
        `OrganizationProvider: ${String(
          orgs ? orgs.length : 0
        )} organizations loaded`
      );
      console.log(
        `OrganizationProvider: ${String(
          kmPostsData && kmPostsData.pipelineKmPosts
            ? kmPostsData.pipelineKmPosts.length
            : "No data"
        )}`
      );
    }
  }, [data || {}, kmPostsData || {}, allPipelineIds]);

  // This should only run on application startup when there is no active org ID yet.
  // If the user does not have an active org defined, grab it from localhost, or fall back to
  // picking the first one.
  useEffect(() => {
    if (organizations && !activeOrgId) {
      // User must belong to at least one org
      if (organizations.length === 0) {
        throw new AppError(
          "Your account is not associated with any organizations",
          "Contact Hifi support to resolve this issue."
        );
      }

      const previouslyActiveOrgId = localStorage.getItem("hifi:activeOrg");
      if (
        previouslyActiveOrgId &&
        findOrgById(previouslyActiveOrgId, organizations)
      ) {
        // If an org was previously active, restore it
        setActiveOrgId(previouslyActiveOrgId);
      } else {
        // Otherwise, just pick the first org
        setActiveOrgId(organizations[0].id);
      }
    }
  }, [activeOrgId, organizations]);

  // Memoize the provider value, otherwise on every re-render the value will change triggering
  // re-renders throughout the app. If memo isn't used, the post markers on the events map will
  // flicker due to organization "changing" on every re-render.
  const providerValue = useMemo(() => {
    if (!organizations || !activeOrgId || isLoading) return initialContext;

    return {
      setActiveOrg,
      organizations,
      activeOrg: findOrgById(activeOrgId, organizations)
    };
  }, [setActiveOrg, organizations, activeOrgId, isLoading]);

  return (
    <OrganizationContext.Provider value={providerValue}>
      {props.children}
    </OrganizationContext.Provider>
  );
};
